import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';

import useSharedStyles from './theme/pbdqShared.styles';
import { pbdqFont } from './theme/pbdqTokens';
import { sdsColors } from './theme/solutionsDesignSystem';
import pbdqRoutes from './pbdqRoutes';

import PbdqNav from './components/PbdqNav.comp';
import PbdqFooter from './components/PbdqFooter.comp';
import { useAgentChat } from './components/PbdqAgentChatContext';

import GetStartedHeroTeam from '../../assets/images/PoweredByDQ/HomeWayDirect_v2.jpg';

/*
 * Get Started — Rebuilt exactly to the "Get Started" page from "Powered by DQ - Website.html"
 * Mockup design, typography (Figtree), layout, cards, pricing calculator, and content.
 */

const useStyles = makeStyles(() => ({
    pageWrapper: {
        fontFamily: pbdqFont,
        color: '#1B2430',
        WebkitFontSmoothing: 'antialiased',
        background: '#FFFFFF',
        minHeight: '100vh',
    },

    // --- SECTION 1: Introduction (Hero) ---
    heroSection: {
        background: 'radial-gradient(70% 90% at 86% 10%, #DCE7F3 0%, rgba(220,231,243,0) 60%), linear-gradient(180deg, #F6F8FB 0%, #FFFFFF 100%)',
    },
    heroInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 64px) clamp(20px, 4vw, 56px) clamp(32px, 4vw, 56px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px clamp(32px, 5vw, 80px)',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
    },
    heroContent: {
        flex: '1 1 440px',
        minWidth: 0,
        display: 'grid',
        gap: 20,
        alignContent: 'start',
    },
    heroTitle: {
        fontSize: 'clamp(36px, 4.2vw, 60px)',
        lineHeight: 1.04,
        letterSpacing: '-0.03em',
        fontWeight: 800,
        margin: 0,
        maxWidth: '16ch',
        fontFamily: pbdqFont,
        color: '#1B2430',
    },
    heroTitleAccent: {
        color: '#ED6628',
    },
    heroLead: {
        fontSize: 'clamp(16px, 1.3vw, 18px)',
        lineHeight: 1.55,
        color: '#5B6470',
        margin: 0,
        maxWidth: '58ch',
        fontFamily: pbdqFont,
    },
    heroArt: {
        flex: '1 1 400px',
        minWidth: 0,
        maxWidth: 640,
        position: 'relative',
        boxSizing: 'border-box',
    },
    heroPhoto: {
        position: 'relative',
        borderRadius: 28,
        overflow: 'hidden',
        background: '#172939',
        aspectRatio: '5/4',
    },
    heroImg: {
        position: 'absolute',
        inset: 0,
        width: '100%',
        height: '100%',
        objectFit: 'cover',
        objectPosition: '50% 45%',
        display: 'block',
        filter: 'saturate(0.78) contrast(1.04)',
    },
    heroPhotoGrad: {
        position: 'absolute',
        inset: 0,
        background: 'linear-gradient(160deg, rgba(23,41,57,0.55) 0%, rgba(23,41,57,0.18) 45%, rgba(23,41,57,0) 70%), linear-gradient(0deg, rgba(23,41,57,0.45) 0%, rgba(23,41,57,0) 40%)',
    },
    heroOrbit1: {
        position: 'absolute',
        inset: 'auto -10% -30% 30%',
        height: '70%',
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.35)',
        pointerEvents: 'none',
    },
    heroOrbit2: {
        position: 'absolute',
        inset: '-20% -20% auto 45%',
        height: '70%',
        borderRadius: '50%',
        border: '1px solid rgba(255,255,255,0.25)',
        pointerEvents: 'none',
    },

    // --- SECTION 2: How will you use Powered by DQ? ---
    useSection: {
        background: '#FFFFFF',
    },
    useInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    sectionEyebrow: {
        display: 'block',
        fontSize: 13,
        lineHeight: 1,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        marginBottom: 20,
    },
    useGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
    },
    useCardSelected: {
        display: 'grid',
        gap: 10,
        padding: 'clamp(22px, 2.6vw, 32px)',
        borderRadius: 20,
        border: '2px solid #ED6628',
        background: '#FFF8F4',
        boxSizing: 'border-box',
    },
    useCardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        alignItems: 'center',
    },
    useCardTitle: {
        fontSize: 20,
        fontWeight: 800,
        letterSpacing: '-0.015em',
        margin: 0,
        color: '#1B2430',
    },
    useSelectedBadge: {
        padding: '5px 11px',
        borderRadius: 999,
        background: '#ED6628',
        color: '#FFFFFF',
        fontSize: 11.5,
        fontWeight: 700,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        whiteSpace: 'nowrap',
    },
    useCardBody: {
        fontSize: 14.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
    },
    useCardLink: {
        display: 'grid',
        gap: 10,
        padding: 'clamp(22px, 2.6vw, 32px)',
        borderRadius: 20,
        border: '1px solid #E6E9EE',
        background: '#FFFFFF',
        textDecoration: 'none',
        color: 'inherit',
        boxSizing: 'border-box',
        transition: 'border-color 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            borderColor: '#1B2430',
        },
    },
    useLinkAction: {
        fontSize: 14,
        fontWeight: 700,
        color: '#A63C0C',
    },

    // --- SECTION 3: Who are you buying for? ---
    whoSection: {
        background: '#F4F6F9',
    },
    whoInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    tabList: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    tabBtn: {
        padding: '12px 18px',
        borderRadius: 999,
        fontFamily: 'inherit',
        fontSize: 14.5,
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'border-color 200ms cubic-bezier(.32,.72,0,1)',
        '&:hover': {
            borderColor: '#1B2430',
        },
    },
    tabPane: {
        marginTop: 36,
        display: 'grid',
        gap: 28,
    },
    tabHeader: {
        display: 'grid',
        gap: 12,
    },
    tabHeading: {
        fontSize: 'clamp(26px, 3vw, 40px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
    },
    tabSubheading: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
    },
    tabCardsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 300px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
        alignItems: 'stretch',
    },
    tabCardsGridWork: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 320px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
        alignItems: 'stretch',
    },
    tabCardsGridPv: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 280px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
        alignItems: 'stretch',
    },

    // Solution Card
    solCard: {
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        padding: 'clamp(22px, 2.6vw, 30px)',
        borderRadius: 24,
        boxSizing: 'border-box',
    },
    solKey: {
        fontSize: 12,
        letterSpacing: '0.06em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#6B7280',
    },
    solTitle: {
        fontSize: 'clamp(20px, 1.9vw, 24px)',
        lineHeight: 1.2,
        letterSpacing: '-0.02em',
        fontWeight: 800,
        margin: 0,
    },
    solDesc: {
        fontSize: 14.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
    },
    solPriceBox: {
        display: 'grid',
        gap: 4,
        padding: '16px 18px',
        borderRadius: 16,
        background: '#FFFFFF',
        border: '1px solid #EEF1F4',
    },
    solPriceMain: {
        fontSize: 30,
        lineHeight: 1,
        fontWeight: 800,
        letterSpacing: '-0.03em',
        margin: 0,
        fontVariantNumeric: 'tabular-nums',
    },
    solPricePer: {
        fontSize: 13,
        lineHeight: 1.45,
        margin: 0,
        color: '#5B6470',
    },
    solPriceLow: {
        fontSize: 13,
        lineHeight: 1.45,
        margin: 0,
        color: '#A63C0C',
        fontWeight: 600,
    },
    solPriceSetup: {
        fontSize: 12.5,
        margin: '6px 0 0',
        color: '#6B7280',
        lineHeight: 1.45,
    },
    solList: {
        listStyle: 'none',
        margin: 0,
        padding: 0,
        display: 'grid',
        gap: 8,
    },
    solListItem: {
        display: 'grid',
        gridTemplateColumns: '18px 1fr',
        gap: 10,
        fontSize: 14,
        lineHeight: 1.5,
    },
    solBest: {
        fontSize: 13.5,
        lineHeight: 1.55,
        margin: 0,
        color: '#5B6470',
        '& strong': {
            color: '#1B2430',
        },
    },
    solDetails: {
        marginTop: 'auto',
        borderTop: '1px solid #EEF1F4',
        paddingTop: 12,
    },
    solSummary: {
        fontSize: 13,
        fontWeight: 700,
        cursor: 'pointer',
        color: '#2A4057',
    },
    solVolGrid: {
        display: 'grid',
        gap: 6,
        marginTop: 10,
    },
    solVolRow: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        fontSize: 13.5,
        fontVariantNumeric: 'tabular-nums',
    },
    solCtas: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 8,
    },
    solCtaPrimary: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 46,
        padding: '0 20px',
        borderRadius: 999,
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        border: '1px solid transparent',
        cursor: 'pointer',
        transition: 'opacity 200ms',
        '&:hover': {
            opacity: 0.9,
            textDecoration: 'none',
        },
    },
    solCtaSecondary: {
        display: 'inline-flex',
        alignItems: 'center',
        height: 46,
        padding: '0 14px',
        color: '#A63C0C',
        textDecoration: 'none',
        fontSize: 14,
        fontWeight: 700,
        whiteSpace: 'nowrap',
        '&:hover': {
            textDecoration: 'underline',
        },
    },

    // Framework engagement cards (under work tab)
    fcGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 260px), 1fr))',
        gap: 12,
    },
    fcCard: {
        padding: '20px 22px',
        borderRadius: 18,
        display: 'grid',
        gap: 8,
        boxSizing: 'border-box',
    },
    fcCardHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 12,
        alignItems: 'baseline',
    },
    fcTitle: {
        fontSize: 16,
        fontWeight: 800,
    },
    fcPrice: {
        fontSize: 18,
        fontWeight: 800,
        fontVariantNumeric: 'tabular-nums',
    },
    fcBody: {
        fontSize: 14,
        lineHeight: 1.55,
        margin: 0,
    },
    fcBest: {
        fontSize: 13,
        lineHeight: 1.5,
        margin: 0,
        color: '#5B6470',
        '& strong': {
            color: '#1B2430',
        },
    },

    // Compare Tables
    tableScroll: {
        overflowX: 'auto',
        border: '1px solid #E6E9EE',
        borderRadius: 20,
    },
    tableTitle: {
        margin: 0,
        padding: '14px 22px',
        fontSize: 13,
        fontWeight: 700,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        color: '#2A4057',
        borderBottom: '1px solid #E6E9EE',
    },
    tableRow4: {
        display: 'grid',
        gridTemplateColumns: '1.6fr 1fr 1fr 1fr',
        gap: 12,
        padding: '12px 22px',
        borderBottom: '1px solid #EEF1F4',
        fontSize: 14,
        boxSizing: 'border-box',
    },
    tableRow3: {
        display: 'grid',
        gridTemplateColumns: '1.8fr 1fr 1fr',
        gap: 12,
        padding: '12px 22px',
        borderBottom: '1px solid #EEF1F4',
        fontSize: 14,
        boxSizing: 'border-box',
    },

    // Government scale box
    govCard: {
        marginTop: 36,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'clamp(24px, 3vw, 48px)',
        padding: 'clamp(24px, 3vw, 44px)',
        borderRadius: 24,
        background: '#172939',
        color: '#FFFFFF',
        boxSizing: 'border-box',
    },
    govColLeft: {
        flex: '1 1 420px',
        minWidth: 0,
        display: 'grid',
        gap: 16,
    },
    govColRight: {
        flex: '1 1 320px',
        minWidth: 0,
        display: 'grid',
        gap: 16,
    },
    govPanel: {
        padding: '20px 22px',
        borderRadius: 18,
        background: 'rgba(255, 255, 255, 0.06)',
        border: '1px solid rgba(255, 255, 255, 0.12)',
        display: 'grid',
        gap: 8,
        boxSizing: 'border-box',
    },
    govPanelTitle: {
        fontSize: 12,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#8AA3BC',
        margin: '0 0 4px',
    },

    // --- SECTION 4: Price your deployment (Calculator) ---
    calcSection: {
        background: '#FFFFFF',
    },
    calcInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    calcHeader: {
        display: 'grid',
        gap: 12,
    },
    calcHeading: {
        fontSize: 'clamp(28px, 3.2vw, 44px)',
        lineHeight: 1.08,
        letterSpacing: '-0.025em',
        fontWeight: 800,
        margin: 0,
    },
    calcSubheading: {
        fontSize: 15.5,
        lineHeight: 1.6,
        margin: 0,
        color: '#5B6470',
    },
    calcBody: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: 'clamp(14px, 1.6vw, 20px)',
        marginTop: 36,
        alignItems: 'flex-start',
    },
    calcCol: {
        flex: '1 1 380px',
        minWidth: 0,
        display: 'grid',
        gap: 'clamp(14px, 1.6vw, 20px)',
        boxSizing: 'border-box',
    },
    calcBox: {
        padding: 'clamp(20px, 2.4vw, 28px)',
        border: '1px solid #E6E9EE',
        borderRadius: 20,
        display: 'grid',
        gap: 12,
        boxSizing: 'border-box',
    },
    calcBoxTitle: {
        fontSize: 12,
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        fontWeight: 700,
        color: '#2A4057',
        margin: 0,
    },
    calcSelect: {
        height: 48,
        padding: '0 40px 0 14px',
        border: '1px solid #D5D9E0',
        borderRadius: 12,
        fontFamily: 'inherit',
        fontSize: 15,
        color: '#1B2430',
        background: '#FFFFFF',
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        backgroundImage: `url("data:image/svg+xml;charset=UTF-8,%3csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%231B2430' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3e%3cpolyline points='6 9 12 15 18 9'%3e%3c/polyline%3e%3c/svg%3e")`,
        backgroundRepeat: 'no-repeat',
        backgroundPosition: 'right 14px center',
        backgroundSize: '16px 16px',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
        cursor: 'pointer',
    },
    calcInput: {
        height: 48,
        padding: '0 14px',
        border: '1px solid #D5D9E0',
        borderRadius: 12,
        fontFamily: 'inherit',
        fontSize: 16,
        color: '#1B2430',
        fontVariantNumeric: 'tabular-nums',
        outline: 'none',
        width: '100%',
        boxSizing: 'border-box',
    },
    calcFieldLabel: {
        display: 'grid',
        gap: 6,
        fontSize: 13,
        fontWeight: 600,
        color: '#5B6470',
    },
    calcDarkCard: {
        padding: 'clamp(22px, 2.6vw, 32px)',
        borderRadius: 24,
        background: '#172939',
        color: '#FFFFFF',
        display: 'grid',
        gap: 14,
        boxSizing: 'border-box',
    },
    calcDarkLine: {
        display: 'flex',
        justifyContent: 'space-between',
        gap: 16,
        padding: '10px 0',
        borderBottom: '1px solid rgba(255,255,255,0.12)',
        fontSize: 14,
    },

    // --- SECTION 5: Creation capacity and add-ons ---
    addonsSection: {
        background: '#FFF8F4',
    },
    addonsInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    addonsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
        gap: 'clamp(14px, 1.6vw, 20px)',
    },

    // --- SECTION 6: Frequently asked questions ---
    faqSection: {
        background: '#FFFFFF',
    },
    faqInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        boxSizing: 'border-box',
    },
    faqGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 340px), 1fr))',
        gap: 12,
    },
    faqItem: {
        padding: '16px 20px',
        border: '1px solid #E6E9EE',
        borderRadius: 16,
        boxSizing: 'border-box',
    },
    faqSummary: {
        fontSize: 15,
        fontWeight: 700,
        cursor: 'pointer',
        lineHeight: 1.4,
    },
    faqAnswer: {
        fontSize: 14.5,
        lineHeight: 1.6,
        margin: '10px 0 0',
        color: '#5B6470',
    },

    // --- SECTION 7: Next step ---
    nextSection: {
        marginTop: 'clamp(24px, 3vw, 40px)',
        background: '#F4F6F9',
        borderTop: '1px solid #E9ECF0',
    },
    nextInner: {
        maxWidth: 1320,
        margin: '0 auto',
        padding: 'clamp(36px, 4.5vw, 56px) clamp(20px, 4vw, 56px)',
        display: 'flex',
        flexWrap: 'wrap',
        gap: '24px 48px',
        alignItems: 'center',
        justifyContent: 'space-between',
        boxSizing: 'border-box',
    },
}));

// Check SVG Icon
const CheckIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#ED6628" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ marginTop: 2, flexShrink: 0 }}>
        <path d="m5 12 5 5L20 7" />
    </svg>
);

// Card styling helper
const cardTransform = (c) => ({
    ...c,
    border: c.featured ? '#ED6628' : '#E6E9EE',
    bg: c.featured ? '#FFF8F4' : '#FFFFFF',
    ctaBg: c.featured ? '#ED6628' : '#1B2430',
    ctaColor: '#FFFFFF',
    ctaBorder: c.featured ? '#ED6628' : '#1B2430',
});

// Compare table row helper
const cmpTransform = (rows) => {
    const dash = '—';
    return rows.map((r, i) => {
        const head = i === 0;
        const col = (v) => (v === dash ? '#B8C0CC' : 'inherit');
        return {
            ...r,
            bg: head ? '#F8F9FB' : '#FFFFFF',
            weight: head ? 700 : 400,
            color: head ? '#6B7280' : '#1B2430',
            aColor: col(r.a),
            bColor: col(r.b),
            cColor: col(r.c),
        };
    });
};

const inc = 'Included';
const dash = '—';
const tints = ['#E1F0F9', '#EDF2D5', '#FAEDDA'];
const inks = ['#1F5F86', '#4E6A12', '#8A5A10'];

// Raw data definitions from mockup
const RAW_SCHOOL_CARDS = [
    {
        k: 'COS Children Assessment · P0',
        h: 'Establish your child online safety baseline.',
        b: 'Measure students’ digital-citizenship readiness, cyber-risk exposure and priority support needs.',
        price: 'US$3.25',
        per: 'per learner for deployments under 10,000',
        low: 'As low as US$1.80 per learner at national scale',
        inc: ['COS Children Assessment for every licensed learner', 'Individual student scorecards', 'School and network dashboards', 'Standard workspaces and reporting'],
        best: 'Schools, school networks, education authorities and governments that need measurement before selecting an intervention.',
        cta: 'Calculate your price',
        cta2: 'Explore the assessment',
        link: pbdqRoutes.cosAssessments,
        vol: [{ b: 'Under 10,000', p: 'US$3.25' }, { b: '10,000–99,999', p: 'US$2.55' }, { b: '100,000–499,999', p: 'US$2.15' }, { b: '500,000+', p: 'US$1.80' }],
    },
    {
        k: 'LearnCreator · LC',
        h: 'Create measurable learning in minutes.',
        b: 'Enable educators to create, adapt and deliver standards-aligned learning programs without the COS assessment layer.',
        price: 'US$8.65',
        per: 'per learner per year for deployments under 10,000',
        low: 'As low as US$6.30 per learner at national scale',
        inc: ['LearnCreator with unlimited creator accounts', 'Program and session creation', 'Embedded impact assessments', 'Standard workspaces, dashboards and delivery', 'One creation credit included for every ten learners'],
        best: 'Schools and education providers that already understand their learning priorities and need the tools to create and deliver programs.',
        cta: 'Calculate your price',
        cta2: 'Explore LearnCreator',
        link: pbdqRoutes.learnCreator,
        vol: [{ b: 'Under 10,000', p: 'US$8.65' }, { b: '10,000–99,999', p: 'US$7.65' }, { b: '100,000–499,999', p: 'US$6.95' }, { b: '500,000+', p: 'US$6.30' }],
    },
    {
        k: 'School COS Readiness · P1B',
        featured: true,
        h: 'Measure what students need. Build targeted learning. Track improvement.',
        b: 'Combine the COS Children Assessment with LearnCreator, program delivery and impact tracking.',
        price: 'US$11.35',
        per: 'per learner per year for deployments under 10,000',
        low: 'As low as US$8.10 per learner at national scale',
        inc: ['COS Children Assessment', 'Individual scorecards and organizational dashboards', 'LearnCreator with unlimited creator accounts', 'Program delivery and impact tracking', 'One creation credit included for every ten learners'],
        best: 'Schools and networks seeking a continuous, evidence-led approach to child online safety and digital citizenship.',
        cta: 'Calculate your price',
        cta2: 'Explore School COS Readiness',
        link: pbdqRoutes.schoolCosReadiness,
        vol: [{ b: 'Under 10,000', p: 'US$11.35' }, { b: '10,000–99,999', p: 'US$10.05' }, { b: '100,000–499,999', p: 'US$9.15' }, { b: '500,000+', p: 'US$8.10' }],
    },
].map(cardTransform);

const SCHOOL_COMPARE = cmpTransform([
    { l: '', a: 'P0', b: 'LearnCreator', c: 'P1B' },
    { l: 'COS Children Assessment', a: inc, b: dash, c: inc },
    { l: 'Student scorecards', a: inc, b: dash, c: inc },
    { l: 'School and network dashboards', a: inc, b: inc, c: inc },
    { l: 'LearnCreator', a: dash, b: inc, c: inc },
    { l: 'Embedded impact assessments', a: dash, b: inc, c: inc },
    { l: 'Program delivery', a: dash, b: inc, c: inc },
    { l: 'Impact tracking', a: dash, b: inc, c: inc },
    { l: 'Included creation credits', a: dash, b: '1 per 10 learners', c: '1 per 10 learners' },
    { l: 'One-time setup', a: 'US$2,500', b: 'US$2,500', c: 'US$2,500' },
    { l: 'Entry unit price', a: 'US$3.25', b: 'US$8.65', c: 'US$11.35' },
    { l: 'Best for', a: 'Measure', b: 'Create and deliver', c: 'Measure, build and track' },
]);

const RAW_WORK_CARDS = [
    {
        k: 'Workforce AI Readiness · P2A',
        h: 'Measure workforce AI readiness.',
        b: 'Define the AI-digital capabilities required across roles, establish an employee baseline and identify investment priorities.',
        price: 'US$10.60',
        per: 'per employee per year for deployments under 5,000',
        low: 'As low as US$3.95 per employee for global deployments',
        inc: ['Workforce AI Readiness assessment', 'Employee and organizational dashboards', 'Capability-gap analysis', 'Role-family mapping', 'Workforce reporting and investment priorities'],
        best: 'Organizations that need to understand workforce readiness before designing or purchasing learning.',
        cta: 'Configure P2A',
        vol: [{ b: 'Under 5,000', p: 'US$10.60' }, { b: '5,000–24,999', p: 'US$8.00' }, { b: '25,000–99,999', p: 'US$5.50' }, { b: '100,000+', p: 'US$3.95' }],
    },
    {
        k: 'Workforce AI Readiness · P2B',
        featured: true,
        h: 'Measure readiness and build targeted capability.',
        b: 'Combine the P2A workforce baseline with LearnCreator so your organization can create and deliver learning targeted to the gaps identified.',
        price: 'US$21.20',
        per: 'per employee per year for deployments under 5,000',
        low: 'As low as US$6.80 per employee for global deployments',
        inc: ['Everything included in P2A', 'LearnCreator with unlimited builder and creator accounts', 'Targeted program creation', 'Learning delivery and impact tracking', 'One creation credit included for every ten employees'],
        best: 'Organizations seeking an end-to-end workforce capability-building system.',
        cta: 'Configure P2B',
        vol: [{ b: 'Under 5,000', p: 'US$21.20' }, { b: '5,000–24,999', p: 'US$16.00' }, { b: '25,000–99,999', p: 'US$11.00' }, { b: '100,000+', p: 'US$6.80' }],
    },
].map(cardTransform);

const FC_CARDS = [
    { k: 'Basic', p: 'US$10,000', b: 'Adopt the DQ framework with your organization’s role families mapped.', best: 'Organizations that want a globally aligned foundation with limited customization.' },
    { k: 'Standard', p: 'US$48,000', b: 'Use FrameworkCreator with guided methodology, configuration and mapping.', best: 'Organizations that need a tailored capacity framework developed through a guided process.' },
    { k: 'Full', p: 'US$100,000', b: 'Develop a fully customized, enterprise-wide AI-digital capacity framework.', best: 'Complex organizations requiring a bespoke framework across multiple roles, business units or markets.' },
].map((f, i) => ({ ...f, tint: tints[i], ink: inks[i] }));

const WORK_COMPARE = cmpTransform([
    { l: '', a: 'P2A', b: 'P2B' },
    { l: 'Workforce readiness assessment', a: inc, b: inc },
    { l: 'Employee and organizational dashboards', a: inc, b: inc },
    { l: 'Capability-gap analysis', a: inc, b: inc },
    { l: 'FrameworkCreator required', a: 'Yes', b: 'Yes' },
    { l: 'LearnCreator', a: dash, b: inc },
    { l: 'Program delivery', a: dash, b: inc },
    { l: 'Impact tracking', a: dash, b: inc },
    { l: 'Included creation credits', a: dash, b: '1 per 10 employees' },
    { l: 'One-time setup', a: 'US$2,500', b: 'US$2,500' },
    { l: 'Entry unit price', a: 'US$10.60', b: 'US$21.20' },
    { l: 'Best for', a: 'Measure', b: 'Measure and build' },
]);

const RAW_PV_CARDS = [
    {
        k: 'Essential validation',
        h: 'Establish credible standards alignment.',
        price: 'US$6,500',
        renew: 'US$2,250',
        inc: ['DQ Stars alignment review', 'Rating decision', 'Certification mark where earned'],
        best: 'Programs requiring independent alignment evidence.',
        cta: 'Request Essential validation',
    },
    {
        k: 'Standard validation',
        featured: true,
        h: 'Validate alignment and measure initial impact.',
        price: 'US$10,500',
        renew: 'US$5,000',
        inc: ['Everything included in Essential', 'Tailored impact assessment', 'Impact measurement for up to 100 participants'],
        best: 'Programs requiring alignment and initial evidence of measurable impact.',
        cta: 'Request Standard validation',
    },
    {
        k: 'Comprehensive validation',
        h: 'Build robust evidence for adoption and scale.',
        price: 'US$17,000',
        renew: 'US$9,000',
        inc: ['Everything included in Standard', 'Impact measurement for up to 1,000 participants', 'Expert review session'],
        best: 'Programs preparing for major procurement, investment or expansion.',
        cta: 'Request Comprehensive validation',
    },
].map(cardTransform);

const PV_COMPARE = cmpTransform([
    { l: '', a: 'Essential', b: 'Standard', c: 'Comprehensive' },
    { l: 'DQ Stars alignment', a: inc, b: inc, c: inc },
    { l: 'Rating and certification mark', a: 'Where earned', b: 'Where earned', c: 'Where earned' },
    { l: 'Tailored impact assessment', a: dash, b: inc, c: inc },
    { l: 'Included impact participants', a: dash, b: '100', c: '1,000' },
    { l: 'Expert review session', a: dash, b: dash, c: inc },
    { l: 'Initial validation', a: 'US$6,500', b: 'US$10,500', c: 'US$17,000' },
    { l: 'Annual renewal', a: 'US$2,250', b: 'US$5,000', c: 'US$9,000' },
]);

const GOV_SOLUTIONS = [
    { k: 'P0', b: 'Measure children’s online safety readiness and cyber-risk exposure' },
    { k: 'P1B', b: 'Measure, build and track child online safety capability' },
    { k: 'LearnCreator', b: 'Enable large-scale program creation and delivery' },
    { k: 'P2A', b: 'Establish workforce AI readiness' },
    { k: 'P2B', b: 'Measure workforce needs and build targeted capability' },
    { k: 'Program Validation', b: 'Validate program quality and measure impact' },
];

const GOV_EXTRAS = [
    'API and single sign-on',
    'LMS integration',
    'Multiple languages',
    'National or enterprise dashboards',
    'Custom frameworks and role mapping',
    'Research and advisory support',
];

const CREDIT_BLOCKS = [
    { b: '100 credits', p: 'US$800', pc: 'US$8.00 per credit' },
    { b: '500 credits', p: 'US$3,250', pc: 'US$6.50 per credit' },
    { b: '2,500 credits', p: 'US$14,000', pc: 'US$5.60 per credit' },
];

const ADDONS = [
    { l: 'Platform Mode — API or SSO', p: 'US$40,000 per deployment' },
    { l: 'LMS integration connector', p: 'US$25,000 per connector' },
    { l: 'Assessment-only localization', p: 'US$6,000 per language' },
    { l: 'Full platform localization', p: 'US$20,000 per language' },
    { l: 'Extra role-family mapping', p: 'US$2,000 per mapping' },
    { l: 'Researcher review or framework alignment', p: 'US$2,800 per program' },
    { l: 'Featured validation registry or co-marketing', p: 'US$5,000 per listing' },
];

const FAQS = [
    { q: 'Is the US$2,500 setup fee annual?', a: 'No. It is charged once to configure and launch the agreed implementation environment.' },
    { q: 'Is there a separate annual platform fee?', a: 'No. There is no separate annual platform-access fee for direct organizational customers. Learner and employee licenses provide access for up to 12 months and are renewed annually.' },
    { q: 'Is FrameworkCreator included in the P2 license price?', a: 'No. Every P2A and P2B deployment requires a separate Basic, Standard or Full FrameworkCreator engagement.' },
    { q: 'Are creation credits included?', a: 'Yes. LearnCreator, P1B and P2B include one creation credit for every ten annual licenses. Additional credits can be purchased in fixed blocks.' },
    { q: 'Does Program Validation require annual licenses?', a: 'No. It is priced per program. Standard and Comprehensive include an initial allowance for impact measurement. Validated programs have an annual renewal fee.' },
    { q: 'Can we purchase P1A directly?', a: 'P1A, in which programs are created and managed centrally by a delivery provider, is available through the DQ Pro partner route. Direct organizational procurement currently provides P1B, enabling the organization’s own educators to create and adapt programs.' },
    { q: 'Can a small school or organization purchase directly?', a: 'Direct procurement is intended for qualifying large institutional, workforce, government and strategic deployments. Smaller deployments may be served through an approved DQ Pro partner.' },
];

const AUDIENCES = [
    { v: 'schools', l: 'Schools and school networks' },
    { v: 'work', l: 'Companies and workforces' },
    { v: 'prov', l: 'Program providers' },
    { v: 'gov', l: 'Government and large-scale initiatives' },
];

const SOLS = {
    P0:  { l: 'COS Children Assessment · P0', unit: 'learners', bands: [[10000, 3.25], [100000, 2.55], [500000, 2.15], [Infinity, 1.80]] },
    LC:  { l: 'LearnCreator · LC', unit: 'learners', bands: [[10000, 8.65], [100000, 7.65], [500000, 6.95], [Infinity, 6.30]] },
    P1B: { l: 'School COS Readiness · P1B', unit: 'learners', bands: [[10000, 11.35], [100000, 10.05], [500000, 9.15], [Infinity, 8.10]] },
    P2A: { l: 'Workforce AI Readiness · P2A', unit: 'employees', fc: true, bands: [[5000, 10.60], [25000, 8.00], [100000, 5.50], [Infinity, 3.95]] },
    P2B: { l: 'Workforce AI Readiness · P2B', unit: 'employees', fc: true, bands: [[5000, 21.20], [25000, 16.00], [100000, 11.00], [Infinity, 6.80]] },
    PV:  { l: 'Program Validation', pv: true, tiers: { Essential: [6500, 2250], Standard: [10500, 5000], Comprehensive: [17000, 9000] } },
};
const FC_TIERS_DICT = { Basic: 10000, Standard: 48000, Full: 100000 };

const PbdqGetStartedPageContent = () => {
    const classes = useStyles();
    const openAgentChat = useAgentChat();

    // Tab state
    const [aud, setAud] = useState('schools');

    // Calculator state
    const [calcState, setCalcState] = useState({
        sol: 'P1B',
        vol: 12000,
        fcTier: 'Basic',
        pvCount: 1,
        pvTier: 'Standard',
    });

    const S = SOLS[calcState.sol] || SOLS['P1B'];
    const isPV = !!S.pv;
    const isP2 = !!S.fc;

    const usd = (n) => 'US$' + Math.round(n || 0).toLocaleString('en-US');
    const usd2 = (n) => 'US$' + (Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    const vol = Math.max(0, Number(calcState.vol) || 0);

    const y1Rows = [{ l: 'Implementation and workspace setup', v: usd(2500), sub: '' }];
    const y2Rows = [];
    let y1 = 2500;
    let y2 = 0;

    if (isPV) {
        const n = Math.max(1, Number(calcState.pvCount) || 1);
        const tierKey = (S.tiers && S.tiers[calcState.pvTier]) ? calcState.pvTier : 'Standard';
        const t = S.tiers[tierKey];
        y1Rows.push({ l: 'Validation — ' + tierKey, v: usd(n * t[0]), sub: n.toLocaleString('en-US') + ' × ' + usd(t[0]) + ' per program' });
        y1 += n * t[0];
        y2Rows.push({ l: 'Program-validation renewal', v: usd(n * t[1]), sub: n.toLocaleString('en-US') + ' × ' + usd(t[1]) + ' per program' });
        y2 += n * t[1];
    } else {
        const b = S.bands.find((band) => vol < band[0]) || S.bands[S.bands.length - 1];
        const unitPrice = b ? b[1] : (S.bands[0] ? S.bands[0][1] : 0);
        const lic = vol * unitPrice;
        const sub = vol.toLocaleString('en-US') + ' × ' + usd2(unitPrice) + ' = ' + usd(lic);
        const label = 'Annual ' + (S.unit === 'employees' ? 'employee' : 'learner') + ' licenses';
        y1Rows.push({ l: label, v: usd(lic), sub });
        y1 += lic;
        y2Rows.push({ l: label, v: usd(lic), sub });
        y2 += lic;
        if (isP2) {
            const f = FC_TIERS_DICT[calcState.fcTier] || FC_TIERS_DICT['Basic'] || 10000;
            y1Rows.push({ l: 'FrameworkCreator — ' + (calcState.fcTier || 'Basic'), v: usd(f), sub: 'per deployment' });
            y1 += f;
        }
    }

    const solOptions = Object.keys(SOLS).map((k) => ({ v: k, l: SOLS[k].l }));
    const volLabel = 'Annual ' + (S.unit === 'employees' ? 'employees' : 'learners');

    const scrollToCalc = (solKey) => {
        if (solKey) {
            setCalcState((prev) => ({ ...prev, sol: solKey }));
        }
        const el = document.getElementById('calculator');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    };

    return (
        <div className={classes.pageWrapper}>
            <PbdqNav />

            {/* SECTION 1: Introduction (Hero) */}
            <section aria-label="Introduction" className={classes.heroSection}>
                <div className={classes.heroInner}>
                    <div className={classes.heroContent}>
                        <h1 className={classes.heroTitle}>
                            Find the right solution for <span className={classes.heroTitleAccent}>your organization.</span>
                        </h1>
                        <p className={classes.heroLead}>
                            Choose what you want to achieve, see what is included and calculate an indicative price based on your deployment size.
                        </p>
                    </div>
                    <div className={classes.heroArt} data-hero-art="">
                        <div className={classes.heroPhoto} data-hero-photo="">
                            <img
                                src={GetStartedHeroTeam}
                                alt="A team meeting in a modern office with laptops"
                                className={classes.heroImg}
                            />
                            <div aria-hidden="true" className={classes.heroPhotoGrad} />
                            <div aria-hidden="true" className={classes.heroOrbit1} />
                            <div aria-hidden="true" className={classes.heroOrbit2} />
                        </div>
                    </div>
                </div>
            </section>

            <main style={{ minWidth: 0 }}>
                {/* SECTION 2: How will you use Powered by DQ? */}
                <section aria-label="How will you use Powered by DQ?" className={classes.useSection}>
                    <div className={classes.useInner}>
                        <span className={classes.sectionEyebrow}>How will you use Powered by DQ?</span>
                        <div className={classes.useGrid}>
                            <div className={classes.useCardSelected}>
                                <div className={classes.useCardHeader}>
                                    <p className={classes.useCardTitle}>Deploy in my organization</p>
                                    <span className={classes.useSelectedBadge}>Selected — this page</span>
                                </div>
                                <p className={classes.useCardBody}>
                                    For organizations purchasing Powered by DQ for their own students, employees, members or programs.
                                </p>
                            </div>
                            <Link to={pbdqRoutes.pricing} className={classes.useCardLink}>
                                <p className={classes.useCardTitle}>Deliver to external clients</p>
                                <p className={classes.useCardBody}>
                                    For organizations that want to resell Powered by DQ, manage client deployments and add their own professional services.
                                </p>
                                <span className={classes.useLinkAction}>Explore the DQ Pro partnership →</span>
                            </Link>
                        </div>
                    </div>
                </section>

                {/* SECTION 3: Who are you buying for? */}
                {/* <section aria-label="Who are you buying for?" className={classes.whoSection}>
                    <div className={classes.whoInner}>
                        <span className={classes.sectionEyebrow}>Who are you buying for?</span>
                        <div role="tablist" className={classes.tabList}>
                            {AUDIENCES.map((a) => {
                                const on = aud === a.v;
                                return (
                                    <button
                                        key={a.v}
                                        type="button"
                                        role="tab"
                                        aria-selected={on}
                                        onClick={() => setAud(a.v)}
                                        className={classes.tabBtn}
                                        style={{
                                            border: `1px solid ${on ? '#1B2430' : '#D5D9E0'}`,
                                            background: on ? '#1B2430' : '#FFFFFF',
                                            color: on ? '#FFFFFF' : '#1B2430',
                                        }}
                                    >
                                        {a.l}
                                    </button>
                                );
                            })}
                        </div>

                        TAB 1: Schools and school networks
                        {aud === 'schools' && (
                            <div className={classes.tabPane}>
                                <div className={classes.tabHeader}>
                                    <h2 className={classes.tabHeading}>Measure student needs, create targeted learning or do both.</h2>
                                    <p className={classes.tabSubheading}>
                                        Choose a solution according to whether you need assessment, learning creation or the complete continuous-improvement cycle.
                                    </p>
                                </div>
                                <div className={classes.tabCardsGrid}>
                                    {RAW_SCHOOL_CARDS.map((c, idx) => (
                                        <div
                                            key={c.k}
                                            className={classes.solCard}
                                            style={{ border: `1px solid ${c.border}`, background: c.bg }}
                                        >
                                            <span className={classes.solKey}>{c.k}</span>
                                            <h3 className={classes.solTitle}>{c.h}</h3>
                                            <p className={classes.solDesc}>{c.b}</p>
                                            <div className={classes.solPriceBox}>
                                                <p className={classes.solPriceMain}>{c.price}</p>
                                                <p className={classes.solPricePer}>{c.per}</p>
                                                <p className={classes.solPriceLow}>{c.low}</p>
                                                <p className={classes.solPriceSetup}>One-time setup: US$2,500</p>
                                            </div>
                                            <ul className={classes.solList}>
                                                {c.inc.map((item) => (
                                                    <li key={item} className={classes.solListItem}>
                                                        <CheckIcon />
                                                        <span>{item}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            <p className={classes.solBest}>
                                                <strong>Best for:</strong> {c.best}
                                            </p>
                                            <details className={classes.solDetails}>
                                                <summary className={classes.solSummary}>Volume pricing</summary>
                                                <div className={classes.solVolGrid}>
                                                    {c.vol.map((v) => (
                                                        <div key={v.b} className={classes.solVolRow}>
                                                            <span style={{ color: '#5B6470' }}>{v.b}</span>
                                                            <strong>{v.p}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            </details>
                                            <div className={classes.solCtas}>
                                                <a
                                                    href="#calculator"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        scrollToCalc(idx === 0 ? 'P0' : idx === 1 ? 'LC' : 'P1B');
                                                    }}
                                                    className={classes.solCtaPrimary}
                                                    style={{ background: c.ctaBg, color: c.ctaColor, borderColor: c.ctaBorder }}
                                                >
                                                    {c.cta}
                                                </a>
                                                <Link to={c.link} className={classes.solCtaSecondary}>
                                                    {c.cta2} →
                                                </Link>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div className={classes.tableScroll}>
                                    <div style={{ minWidth: 640 }}>
                                        <p className={classes.tableTitle}>Compare school solutions</p>
                                        {SCHOOL_COMPARE.map((r, i) => (
                                            <div
                                                key={i}
                                                className={classes.tableRow4}
                                                style={{ background: r.bg, fontWeight: r.weight, color: r.color }}
                                            >
                                                <span>{r.l}</span>
                                                <span style={{ color: r.aColor }}>{r.a}</span>
                                                <span style={{ color: r.bColor }}>{r.b}</span>
                                                <span style={{ color: r.cColor }}>{r.c}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        TAB 2: Companies and workforces
                        {aud === 'work' && (
                            <div className={classes.tabPane}>
                                <div className={classes.tabHeader}>
                                    <h2 className={classes.tabHeading}>Establish workforce readiness—or measure and build together.</h2>
                                    <p className={classes.tabSubheading}>
                                        Every Workforce AI Readiness deployment includes employee licensing and requires an appropriate FrameworkCreator engagement.
                                    </p>
                                </div>
                                <div className={classes.tabCardsGridWork}>
                                    {RAW_WORK_CARDS.map((c, idx) => (
                                        <div
                                            key={c.k}
                                            className={classes.solCard}
                                            style={{ border: `1px solid ${c.border}`, background: c.bg }}
                                        >
                                            <span className={classes.solKey}>{c.k}</span>
                                            <h3 className={classes.solTitle}>{c.h}</h3>
                                            <p className={classes.solDesc}>{c.b}</p>
                                            <div className={classes.solPriceBox}>
                                                <p className={classes.solPriceMain}>{c.price}</p>
                                                <p className={classes.solPricePer}>{c.per}</p>
                                                <p className={classes.solPriceLow}>{c.low}</p>
                                                <p className={classes.solPriceSetup}>One-time setup: US$2,500 · FrameworkCreator from US$10,000 per deployment</p>
                                            </div>
                                            <ul className={classes.solList}>
                                                {c.inc.map((item) => (
                                                    <li key={item} className={classes.solListItem}>
                                                        <CheckIcon />
                                                        <span>{item}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            <p className={classes.solBest}>
                                                <strong>Best for:</strong> {c.best}
                                            </p>
                                            <details className={classes.solDetails}>
                                                <summary className={classes.solSummary}>Employee volume pricing</summary>
                                                <div className={classes.solVolGrid}>
                                                    {c.vol.map((v) => (
                                                        <div key={v.b} className={classes.solVolRow}>
                                                            <span style={{ color: '#5B6470' }}>{v.b}</span>
                                                            <strong>{v.p}</strong>
                                                        </div>
                                                    ))}
                                                </div>
                                            </details>
                                            <div className={classes.solCtas}>
                                                <a
                                                    href="#calculator"
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        scrollToCalc(idx === 0 ? 'P2A' : 'P2B');
                                                    }}
                                                    className={classes.solCtaPrimary}
                                                    style={{ background: c.ctaBg, color: c.ctaColor, borderColor: c.ctaBorder }}
                                                >
                                                    {c.cta}
                                                </a>
                                                <Link to={pbdqRoutes.workforceAiReadiness} className={classes.solCtaSecondary}>
                                                    Explore the solution →
                                                </Link>
                                            </div>
                                        </div>
                                    ))}
                                </div>

                                <div style={{ display: 'grid', gap: 14 }}>
                                    <h3 style={{ fontSize: 'clamp(20px, 2vw, 26px)', lineHeight: 1.2, letterSpacing: '-0.02em', fontWeight: 800, margin: 0 }}>
                                        Choose your framework engagement
                                    </h3>
                                    <p style={{ fontSize: 14.5, lineHeight: 1.55, margin: 0, color: '#5B6470' }}>
                                        Every P2A and P2B deployment requires one FrameworkCreator tier.
                                    </p>
                                    <div className={classes.fcGrid}>
                                        {FC_CARDS.map((f) => (
                                            <div key={f.k} className={classes.fcCard} style={{ background: f.tint }}>
                                                <div className={classes.fcCardHeader}>
                                                    <span className={classes.fcTitle}>{f.k}</span>
                                                    <span className={classes.fcPrice} style={{ color: f.ink }}>{f.p}</span>
                                                </div>
                                                <p className={classes.fcBody}>{f.b}</p>
                                                <p className={classes.fcBest}>
                                                    <strong>Best for:</strong> {f.best}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>

                                <div className={classes.tableScroll}>
                                    <div style={{ minWidth: 560 }}>
                                        <p className={classes.tableTitle}>Compare workforce solutions</p>
                                        {WORK_COMPARE.map((r, i) => (
                                            <div
                                                key={i}
                                                className={classes.tableRow3}
                                                style={{ background: r.bg, fontWeight: r.weight, color: r.color }}
                                            >
                                                <span>{r.l}</span>
                                                <span style={{ color: r.aColor }}>{r.a}</span>
                                                <span style={{ color: r.bColor }}>{r.b}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        TAB 3: Program providers
                        {aud === 'prov' && (
                            <div className={classes.tabPane}>
                                <div className={classes.tabHeader}>
                                    <h2 className={classes.tabHeading}>Validate program quality and demonstrate impact.</h2>
                                    <p className={classes.tabSubheading}>
                                        DQ Stars independently evaluates how an existing program aligns with recognized AI-digital standards. Standard and Comprehensive validation add tailored impact measurement.
                                    </p>
                                </div>
                                <div className={classes.tabCardsGridPv}>
                                    {RAW_PV_CARDS.map((c) => (
                                        <div
                                            key={c.k}
                                            className={classes.solCard}
                                            style={{ border: `1px solid ${c.border}`, background: c.bg }}
                                        >
                                            <span className={classes.solKey}>{c.k}</span>
                                            <h3 className={classes.solTitle}>{c.h}</h3>
                                            <div className={classes.solPriceBox}>
                                                <p className={classes.solPriceMain}>{c.price}</p>
                                                <p style={{ fontSize: 13, margin: 0, color: '#5B6470' }}>per program</p>
                                                <p className={classes.solPriceSetup}>
                                                    One-time setup: US$2,500 · Annual renewal: {c.renew} per program
                                                </p>
                                            </div>
                                            <ul className={classes.solList}>
                                                {c.inc.map((item) => (
                                                    <li key={item} className={classes.solListItem}>
                                                        <CheckIcon />
                                                        <span>{item}</span>
                                                    </li>
                                                ))}
                                            </ul>
                                            <p className={classes.solBest} style={{ marginBottom: 'auto' }}>
                                                <strong>Best for:</strong> {c.best}
                                            </p>
                                            <Link
                                                to={pbdqRoutes.programValidation}
                                                className={classes.solCtaPrimary}
                                                style={{
                                                    alignSelf: 'flex-start',
                                                    background: c.ctaBg,
                                                    color: c.ctaColor,
                                                    borderColor: c.ctaBorder,
                                                }}
                                            >
                                                {c.cta}
                                            </Link>
                                        </div>
                                    ))}
                                </div>

                                <div className={classes.tableScroll}>
                                    <div style={{ minWidth: 640 }}>
                                        <p className={classes.tableTitle}>Compare validation levels</p>
                                        {PV_COMPARE.map((r, i) => (
                                            <div
                                                key={i}
                                                className={classes.tableRow4}
                                                style={{ background: r.bg, fontWeight: r.weight, color: r.color }}
                                            >
                                                <span>{r.l}</span>
                                                <span style={{ color: r.aColor }}>{r.a}</span>
                                                <span style={{ color: r.bColor }}>{r.b}</span>
                                                <span style={{ color: r.cColor }}>{r.c}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <em style={{ fontSize: 13.5, lineHeight: 1.55, color: '#6B7280' }}>
                                    Each validation covers a program of up to 20 hours. Additional program duration is charged at US$400 per hour.
                                </em>
                            </div>
                        )}

                        TAB 4: Government and large-scale initiatives
                        {aud === 'gov' && (
                            <div className={classes.govCard}>
                                <div className={classes.govColLeft}>
                                    <h2 style={{ fontSize: 'clamp(26px, 3vw, 40px)', lineHeight: 1.08, letterSpacing: '-0.025em', fontWeight: 800, margin: 0 }}>
                                        Configure Powered by DQ for institutional scale.
                                    </h2>
                                    <p style={{ fontSize: 15.5, lineHeight: 1.6, margin: 0, color: '#D9E3EE' }}>
                                        Governments, education authorities, workforce agencies, foundations and strategic initiatives can deploy the same solutions across large populations, multiple organizations or national systems.
                                    </p>
                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, marginTop: 8 }}>
                                        <button
                                            type="button"
                                            onClick={openAgentChat}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                height: 50,
                                                padding: '0 22px',
                                                borderRadius: 999,
                                                background: '#ED6628',
                                                color: '#FFFFFF',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: 15,
                                                fontWeight: 700,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Request a custom proposal
                                        </button>
                                        <button
                                            type="button"
                                            onClick={openAgentChat}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                height: 50,
                                                padding: '0 22px',
                                                borderRadius: 999,
                                                background: 'transparent',
                                                color: '#FFFFFF',
                                                border: '1px solid #5D7A96',
                                                cursor: 'pointer',
                                                fontSize: 15,
                                                fontWeight: 600,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Speak with our team
                                        </button>
                                    </div>
                                </div>
                                <div className={classes.govColRight}>
                                    <div className={classes.govPanel}>
                                        <p className={classes.govPanelTitle}>Choose according to the population and outcome</p>
                                        {GOV_SOLUTIONS.map((g) => (
                                            <span key={g.k} style={{ fontSize: 14, lineHeight: 1.5 }}>
                                                <strong style={{ color: '#FFFFFF' }}>{g.k}: </strong>
                                                <span style={{ color: '#D9E3EE' }}>{g.b}</span>
                                            </span>
                                        ))}
                                    </div>
                                    <div className={classes.govPanel}>
                                        <p className={classes.govPanelTitle}>Large deployments may also include</p>
                                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                                            {GOV_EXTRAS.map((g) => (
                                                <span
                                                    key={g}
                                                    style={{
                                                        padding: '6px 11px',
                                                        borderRadius: 999,
                                                        background: 'rgba(255, 255, 255, 0.1)',
                                                        fontSize: 12.5,
                                                        fontWeight: 600,
                                                        whiteSpace: 'nowrap',
                                                    }}
                                                >
                                                    {g}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </section> */}

                {/* SECTION 4: Price your deployment (Calculator) */}
                {/* <section id="calculator" aria-label="Price your deployment" className={classes.calcSection}>
                    <div className={classes.calcInner}>
                        <span className={classes.sectionEyebrow}>Price your deployment</span>
                        <div className={classes.calcHeader}>
                            <h2 className={classes.calcHeading}>
                                Calculate your indicative first-year cost.
                            </h2>
                            <p className={classes.calcSubheading}>
                                Choose one solution and enter your expected deployment size.
                            </p>
                        </div>
                        <div className={classes.calcBody}>
                            <div className={classes.calcCol}>
                                <div className={classes.calcBox}>
                                    <p className={classes.calcBoxTitle}>01 · Select your solution</p>
                                    <select
                                        value={calcState.sol}
                                        onChange={(e) => {
                                            const val = e.target.value;
                                            setCalcState((prev) => ({ ...prev, sol: val }));
                                        }}
                                        aria-label="Solution"
                                        className={classes.calcSelect}
                                    >
                                        {solOptions.map((o) => (
                                            <option key={o.v} value={o.v}>{o.l}</option>
                                        ))}
                                    </select>
                                </div>

                                <div className={classes.calcBox}>
                                    <p className={classes.calcBoxTitle}>02 · Enter your volume</p>
                                    {!isPV && (
                                        <label className={classes.calcFieldLabel}>
                                            {volLabel}
                                            <input
                                                type="number"
                                                min="0"
                                                step="100"
                                                value={calcState.vol === '' ? '' : calcState.vol}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setCalcState((prev) => ({ ...prev, vol: val === '' ? '' : Math.max(0, Number(val)) }));
                                                }}
                                                className={classes.calcInput}
                                            />
                                        </label>
                                    )}

                                    {isP2 && (
                                        <label className={classes.calcFieldLabel}>
                                            FrameworkCreator tier
                                            <select
                                                value={calcState.fcTier}
                                                onChange={(e) => {
                                                    const val = e.target.value;
                                                    setCalcState((prev) => ({ ...prev, fcTier: val }));
                                                }}
                                                className={classes.calcSelect}
                                            >
                                                <option value="Basic">Basic — US$10,000</option>
                                                <option value="Standard">Standard — US$48,000</option>
                                                <option value="Full">Full — US$100,000</option>
                                            </select>
                                        </label>
                                    )}

                                    {isPV && (
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 160px), 1fr))', gap: 12 }}>
                                            <label className={classes.calcFieldLabel}>
                                                Number of programs
                                                <input
                                                    type="number"
                                                    min="1"
                                                    value={calcState.pvCount === '' ? '' : calcState.pvCount}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setCalcState((prev) => ({ ...prev, pvCount: val === '' ? '' : Math.max(1, Number(val)) }));
                                                    }}
                                                    className={classes.calcInput}
                                                />
                                            </label>
                                            <label className={classes.calcFieldLabel}>
                                                Validation level
                                                <select
                                                    value={calcState.pvTier}
                                                    onChange={(e) => {
                                                        const val = e.target.value;
                                                        setCalcState((prev) => ({ ...prev, pvTier: val }));
                                                    }}
                                                    className={classes.calcSelect}
                                                >
                                                    <option value="Essential">Essential</option>
                                                    <option value="Standard">Standard</option>
                                                    <option value="Comprehensive">Comprehensive</option>
                                                </select>
                                            </label>
                                        </div>
                                    )}
                                    <em style={{ fontSize: 12.5, lineHeight: 1.55, color: '#6B7280' }}>
                                        There is no separate annual platform-access fee. Learner and employee licenses provide access for up to 12 months and are renewed annually. FrameworkCreator is priced per deployment. Program Validation has its own annual renewal fee.
                                    </em>
                                </div>
                            </div>

                            <div className={classes.calcCol}>
                                <div className={classes.calcDarkCard}>
                                    <p style={{ fontSize: 12, letterSpacing: '0.08em', textTransform: 'uppercase', fontWeight: 700, color: '#8AA3BC', margin: 0 }}>
                                        03 · Review your price
                                    </p>
                                    <p style={{ fontSize: 14, fontWeight: 700, margin: '4px 0 0' }}>First year</p>
                                    <div style={{ display: 'grid' }}>
                                        {y1Rows.map((r, i) => (
                                            <div key={i} className={classes.calcDarkLine}>
                                                <span>
                                                    <span style={{ display: 'block' }}>{r.l}</span>
                                                    {r.sub && (
                                                        <span style={{ display: 'block', fontSize: 12, color: '#8AA3BC', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                                                            {r.sub}
                                                        </span>
                                                    )}
                                                </span>
                                                <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.v}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16 }}>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: '#B7C8DA' }}>Estimated first-year total</span>
                                        <span style={{ fontSize: 'clamp(28px, 2.8vw, 38px)', fontWeight: 800, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>
                                            {usd(y1)}
                                        </span>
                                    </div>

                                    <p style={{ fontSize: 14, fontWeight: 700, margin: '12px 0 0', paddingTop: 14, borderTop: '1px solid rgba(255,255,255,0.18)' }}>
                                        From year two
                                    </p>
                                    <div style={{ display: 'grid' }}>
                                        {y2Rows.map((r, i) => (
                                            <div key={i} className={classes.calcDarkLine}>
                                                <span>
                                                    <span style={{ display: 'block' }}>{r.l}</span>
                                                    {r.sub && (
                                                        <span style={{ display: 'block', fontSize: 12, color: '#8AA3BC', marginTop: 2, fontVariantNumeric: 'tabular-nums' }}>
                                                            {r.sub}
                                                        </span>
                                                    )}
                                                </span>
                                                <span style={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>{r.v}</span>
                                            </div>
                                        ))}
                                    </div>

                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 16 }}>
                                        <span style={{ fontSize: 13, fontWeight: 600, color: '#B7C8DA' }}>Estimated annual renewal</span>
                                        <span style={{ fontSize: 'clamp(22px, 2.2vw, 28px)', fontWeight: 800, letterSpacing: '-0.03em', fontVariantNumeric: 'tabular-nums' }}>
                                            {usd(y2)}
                                        </span>
                                    </div>

                                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                                        <button
                                            type="button"
                                            onClick={openAgentChat}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                height: 48,
                                                padding: '0 20px',
                                                borderRadius: 999,
                                                background: '#ED6628',
                                                color: '#FFFFFF',
                                                border: 'none',
                                                cursor: 'pointer',
                                                fontSize: 14.5,
                                                fontWeight: 700,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Request a formal quotation
                                        </button>
                                        <button
                                            type="button"
                                            onClick={openAgentChat}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                height: 48,
                                                padding: '0 20px',
                                                borderRadius: 999,
                                                background: 'transparent',
                                                color: '#FFFFFF',
                                                border: '1px solid #5D7A96',
                                                cursor: 'pointer',
                                                fontSize: 14.5,
                                                fontWeight: 600,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            Speak with our team
                                        </button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section> */}

                {/*
                SECTION 5: Creation capacity and add-ons
                <section aria-label="Creation capacity and add-ons" className={classes.addonsSection}>
                    <div className={classes.addonsInner}>
                        <div className={classes.addonsGrid}>
                            <div style={{ padding: 'clamp(24px, 3vw, 36px)', borderRadius: 24, background: '#F4F6F9', display: 'grid', gap: 16, alignContent: 'start', boxSizing: 'border-box' }}>
                                <span className={classes.sectionEyebrow} style={{ marginBottom: 16 }}>Creation capacity</span>
                                <h2 style={{ fontSize: 'clamp(22px, 2.3vw, 30px)', lineHeight: 1.12, letterSpacing: '-0.02em', fontWeight: 800, margin: 0 }}>
                                    Creation credits are included with every LearnCreator deployment.
                                </h2>
                                <p style={{ fontSize: 15, lineHeight: 1.6, margin: 0, color: '#5B6470' }}>
                                    LearnCreator, P1B and P2B include one creation credit for every ten learner or employee licenses.
                                </p>
                                <p style={{ fontSize: 15, lineHeight: 1.6, margin: 0, color: '#5B6470' }}>
                                    One credit supports the creation of approximately one 45-minute learning session. Credits are pooled across the deployment, and standard creator accounts are unlimited.
                                </p>
                                <div style={{ display: 'grid', gap: 8, paddingTop: 12, borderTop: '1px solid #D9DEE5' }}>
                                    <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#6B7280' }}>
                                        Additional creation credits
                                    </span>
                                    {CREDIT_BLOCKS.map((c) => (
                                        <div key={c.b} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: 12, fontSize: 14, fontVariantNumeric: 'tabular-nums' }}>
                                            <span style={{ fontWeight: 600 }}>{c.b}</span>
                                            <span style={{ fontWeight: 700 }}>{c.p}</span>
                                            <span style={{ color: '#6B7280' }}>{c.pc}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div style={{ padding: 'clamp(24px, 3vw, 36px)', border: '1px solid #E6E9EE', borderRadius: 24, background: '#FFFFFF', display: 'grid', gap: 16, alignContent: 'start', boxSizing: 'border-box' }}>
                                <span className={classes.sectionEyebrow} style={{ marginBottom: 16 }}>Optional add-ons</span>
                                <div style={{ display: 'grid', borderTop: '1px solid #E6E9EE' }}>
                                    {ADDONS.map((a) => (
                                        <div key={a.l} style={{ display: 'flex', justifyContent: 'space-between', gap: 16, padding: '11px 0', borderBottom: '1px solid #EEF1F4', fontSize: 14 }}>
                                            <span>{a.l}</span>
                                            <span style={{ fontWeight: 700, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }}>{a.p}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    </div>
                </section> */}

                {/*
                SECTION 6: Frequently asked questions
                <section aria-label="Frequently asked questions" className={classes.faqSection}>
                    <div className={classes.faqInner}>
                        <span className={classes.sectionEyebrow}>Frequently asked</span>
                        <div className={classes.faqGrid}>
                            {FAQS.map((f, i) => (
                                <details key={i} className={classes.faqItem}>
                                    <summary className={classes.faqSummary}>{f.q}</summary>
                                    <p className={classes.faqAnswer}>{f.a}</p>
                                </details>
                            ))}
                        </div>
                    </div>
                </section> */}
            </main>

            {/* SECTION 7: Next step */}
            <section aria-label="Next step" className={classes.nextSection}>
                <div className={classes.nextInner}>
                    <div style={{ flex: '1 1 420px', minWidth: 0, display: 'grid', gap: 12 }}>
                        <h2 style={{ fontSize: 'clamp(28px, 3.2vw, 44px)', lineHeight: 1.08, letterSpacing: '-0.03em', fontWeight: 800, margin: 0, maxWidth: '22ch' }}>
                            Start with the outcome your organization needs.
                        </h2>
                        <p style={{ fontSize: 15.5, lineHeight: 1.6, margin: 0, color: '#5B6470', maxWidth: '60ch' }}>
                            Measure readiness. Create targeted learning. Build workforce capability. Validate program quality and impact.
                        </p>
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12 }}>
                        {/* <a
                            href="#calculator"
                            onClick={(e) => {
                                e.preventDefault();
                                scrollToCalc();
                            }}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                height: 52,
                                padding: '0 24px',
                                borderRadius: 999,
                                background: '#ED6628',
                                color: '#FFFFFF',
                                textDecoration: 'none',
                                fontSize: 15,
                                fontWeight: 700,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Calculate your price
                        </a> */}
                        <button
                            type="button"
                            onClick={openAgentChat}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                height: 52,
                                padding: '0 24px',
                                borderRadius: 999,
                                background: '#FFFFFF',
                                color: '#1B2430',
                                border: '1px solid #D5D9E0',
                                cursor: 'pointer',
                                fontSize: 15,
                                fontWeight: 600,
                                whiteSpace: 'nowrap',
                            }}
                        >
                            Speak with our team
                        </button>
                    </div>
                </div>
            </section>

            <PbdqFooter />
        </div>
    );
};

export default PbdqGetStartedPageContent;
